import { AdminError, authClient, toAdminError } from '@/lib/auth';

/**
 * The two calls of the request-access flow, each a route of the auth Worker's own admin API
 * (MathGaps/schools `services/auth/src/admin-access.ts`, #1445): `POST /admin/request-access`
 * files the ask, `GET /admin/my-request` reads the caller's own back. Both are reached with
 * `authClient.$fetch`, as the Permissions page reaches `/admin/permissions`
 * (`src/shell/permissions/staff.ts`), and neither takes an argument: the uid and the address
 * come off the session, so the only request anybody can file or read is their own.
 *
 * What the Worker owns and this does not (`request-access-is-a-page` D7): the timestamp, which
 * is the server's and not the browser's (review F16); the email allow-list, which a client-side
 * one would only be theatre of; and the audit line. Filing grants nothing — being approved is
 * the separate event, answered from `src/shell/permissions/RequestsTab.tsx`.
 */

/** What became of an ask. A rejected one is a recorded outcome, not a missing row. */
export type RequestStatus = 'pending' | 'approved' | 'rejected';

const STATUSES: readonly string[] = ['pending', 'approved', 'rejected'];

/** A word this build does not know reads as `pending`: nobody is told they were refused on it. */
export function statusOf(wire: unknown): RequestStatus | null {
  if (wire == null) return null;
  return STATUSES.includes(wire as string)
    ? (wire as RequestStatus)
    : 'pending';
}

/** The request row, field for field as the Worker answers one (`admin-access.ts` › `Request`). */
export interface AdminAccessRequest {
  /** The server's stamp, ISO, or `null` for no request on file. */
  adminAccessRequestedAt: string | null;
  status: RequestStatus | null;
  respondedAt: string | null;
  /** The approver's uid; the Worker keeps no name, so a page joins it to the staff list or says nothing. */
  respondedBy: string | null;
}

/**
 * A stamp as it actually arrives, which is not what the Worker sent. `authClient.$fetch`
 * parses with Better Auth's own reviver, and that reviver turns **every** ISO-8601 string
 * in the body into a `Date` (`better-auth/dist/client/parser.mjs` › `betterJSONParse`,
 * `parseDates` defaulting true). The Worker answers ISO text (`admin-access.ts` › `stamp`);
 * by the time this reads it, it is a `Date` object.
 *
 * So a stamp is `unknown` here and narrowed once, rather than declared `string` and
 * believed. `?? null` does not catch this — a `Date` is not nullish — which is how
 * `queueOf`'s `toSorted` came to call `localeCompare` on one and throw the Permissions page
 * into the shell's fallback.
 */
function iso(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * The answer as it arrives, before `requestOf` reads it. Every field is `unknown`: the
 * reviver above rewrites the body's types, so a narrower declaration here would be a
 * promise the wire does not keep and the compiler cannot check.
 */
export interface RequestWire {
  adminAccessRequestedAt?: unknown;
  status?: unknown;
  respondedAt?: unknown;
  respondedBy?: unknown;
}

export function requestOf(
  wire: RequestWire | null | undefined,
): AdminAccessRequest {
  return {
    adminAccessRequestedAt: iso(wire?.adminAccessRequestedAt),
    status: statusOf(wire?.status),
    respondedAt: iso(wire?.respondedAt),
    respondedBy:
      typeof wire?.respondedBy === 'string' ? wire.respondedBy : null,
  };
}

/**
 * File the request. Asking twice is safe and changes nothing — not the stamp the queue sorts by,
 * so re-asking jumps no line, and not an outcome already recorded.
 *
 * The row the route answers with is thrown away deliberately: the "received" face is drawn from
 * the self-read alone (D3), so nothing a write echoed can put it on the screen.
 */
export async function fileAdminAccessRequest(): Promise<void> {
  const { error } = await authClient.$fetch('/admin/request-access', {
    method: 'POST',
    body: {},
  });
  if (error) throw toAdminError(error);
}

/** The caller's own request, by the session's uid; a row nobody filed answers every field null. */
export async function readAdminAccessRequest(): Promise<AdminAccessRequest> {
  const { data, error } = await authClient.$fetch<{
    request: RequestWire | null;
  }>('/admin/my-request', { method: 'GET' });
  if (error) throw toAdminError(error);
  return requestOf(data?.request);
}

/* Keyed on the Worker's machine code first (`staff-holds-the-grants` D6), then on the status. */
const SENTENCES: Record<string, string> = {
  not_allowed:
    'That email address cannot be given admin access. Ask someone at Tutero to make your account for you.',
  already_admin: 'This account already has admin access. Reload the page.',
  anonymous: 'You are not signed in. Reload the page and try again.',
};

const BY_STATUS: Record<number, string> = {
  0: 'The sign-in service could not be reached.',
  401: 'Your session has expired. Sign in again.',
};

/** Why a file or a read was refused, in the requester's own words. */
export function said(error: unknown): string {
  if (error instanceof AdminError) {
    return (
      SENTENCES[error.code ?? ''] ?? BY_STATUS[error.status] ?? error.message
    );
  }
  if (error instanceof Error && error.message !== '') return error.message;
  return 'Please try again.';
}
